import { useLocalization } from "@/hooks";

export const useDatetimeUtils = () => {
  const { i18n, locale } = useLocalization();

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return i18n.t("misc.secondsShort", { count: seconds });

    if (seconds < 3600) {
      return i18n.t("misc.minutesShort", { count: Math.round(seconds / 60) });
    }

    if (seconds < 86400) {
      const h = Math.floor(seconds / 3600);
      const m = Math.round((seconds % 3600) / 60);
      const hPart = i18n.t("misc.hoursShort", { count: h });
      return m > 0
        ? `${hPart} ${i18n.t("misc.minutesShort", { count: m })}`
        : hPart;
    }

    const d = Math.floor(seconds / 86400);
    const h = Math.round((seconds % 86400) / 3600);
    const dPart = i18n.t("misc.daysShort", { count: d });
    return h > 0
      ? `${dPart} ${i18n.t("misc.hoursShort", { count: h })}`
      : dPart;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatDateShort = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString(locale, {
      day: "numeric",
      month: "short",
      year: "2-digit",
    });
  };

  const formatDateTime = (date: Date | null | undefined): string => {
    if (!date) return i18n.t("misc.unknown");
    return new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(date);
  };

  return {
    formatDate,
    formatDateShort,
    formatDateTime,
    formatDuration,
  };
};
